import React from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { MediaInput } from "@/components/bi/MediaInput";
import { Row, ColorField } from "@/components/bi/StyleEditors";
import { ArrowDownWideNarrow, MessageSquare, Paintbrush, Plus, X, Type as TypeIcon, Image as ImageIcon, SlidersHorizontal, Grid3x3, ChevronUp, ChevronDown } from "lucide-react";

// ---------- Item 182 v2: chart sorting (any measure, multi-level) + Item 179: friendly date labels ----------
const SORT_MODES = [
  { value: "none", label: "As queried" },
  { value: "cat_asc", label: "Category A → Z" },
  { value: "cat_desc", label: "Category Z → A" },
  { value: "measure_desc", label: "Value high → low" },
  { value: "measure_asc", label: "Value low → high" },
];
export function SortEditor({ config, opts, patchOpts, isDateDim }) {
  const measures = config.measures || [];
  const measKey = (m) => m.key || m.alias || `${m.agg || "sum"}_${m.field}`;
  const measLabel = (m) => m.key || `${m.agg || "sum"} of ${m.field}`;
  const then = opts.sortThen || {};
  const setThen = (patch) => patchOpts({ sortThen: { ...then, ...patch } });
  const measureSel = (value, onChange, testid) => (
    <Select value={value || measKey(measures[0] || {})} onValueChange={onChange}>
      <SelectTrigger data-testid={testid} className="bg-[color:var(--surface)] w-[150px] h-7 text-xs"><SelectValue /></SelectTrigger>
      <SelectContent>{measures.map((m, i) => <SelectItem key={i} value={measKey(m)}>{measLabel(m)}</SelectItem>)}</SelectContent>
    </Select>
  );
  return (
    <div data-testid="sort-editor" className="space-y-1.5 rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-3">
      <Label className="text-xs text-[color:var(--ink-2)] flex items-center gap-1.5"><ArrowDownWideNarrow className="w-3.5 h-3.5" />Sorting</Label>
      <Row label="Sort by">
        <Select value={opts.sortBy || "none"} onValueChange={(v) => patchOpts({ sortBy: v === "none" ? "" : v })}>
          <SelectTrigger data-testid="chart-sort-by" className="bg-[color:var(--surface)] w-[150px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{SORT_MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
        </Select>
      </Row>
      {["measure_asc", "measure_desc"].includes(opts.sortBy) && measures.length > 0 && (
        <Row label="Sort measure">{measureSel(opts.sortMeasure, (v) => patchOpts({ sortMeasure: v }), "chart-sort-measure")}</Row>
      )}
      {opts.sortBy && opts.sortBy !== "none" && (
        <>
          <Row label="Then by">
            <Select value={then.by || "none"} onValueChange={(v) => setThen({ by: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="chart-sort-then" className="bg-[color:var(--surface)] w-[150px] h-7 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>{SORT_MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
            </Select>
          </Row>
          {["measure_asc", "measure_desc"].includes(then.by) && measures.length > 0 && (
            <Row label="Then measure">{measureSel(then.measure, (v) => setThen({ measure: v }), "chart-sort-then-measure")}</Row>
          )}
        </>
      )}
      {isDateDim && (
        <Row label="Date labels">
          <Select value={opts.dateLabelFormat || "auto"} onValueChange={(v) => patchOpts({ dateLabelFormat: v })}>
            <SelectTrigger data-testid="date-label-format" className="bg-[color:var(--surface)] w-[150px] h-7 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Friendly (Jan 2025 / 01 Jan)</SelectItem>
              <SelectItem value="d_mon_yyyy">Day + year (15 Jan 2025)</SelectItem>
              <SelectItem value="month_yyyy">Long (January 2025)</SelectItem>
              <SelectItem value="raw">Raw (2025-01)</SelectItem>
            </SelectContent>
          </Select>
        </Row>
      )}
    </div>
  );
}

// ---------- Item 181: tooltip visual styling ----------
export function TooltipStyleEditor({ opts, patchOpts }) {
  const ts = opts.tooltipStyle || {};
  const set = (patch) => patchOpts({ tooltipStyle: { ...ts, ...patch } });
  return (
    <div data-testid="tooltip-style-editor" className="space-y-1.5 rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-3">
      <div className="flex items-center justify-between">
        <Label className="text-xs text-[color:var(--ink-2)] flex items-center gap-1.5"><MessageSquare className="w-3.5 h-3.5" />Tooltip appearance</Label>
        <Switch data-testid="tooltip-enabled" checked={ts.enabled !== false} onCheckedChange={(v) => set({ enabled: v })} />
      </div>
      {ts.enabled !== false && (
        <>
          <Row label="Background"><ColorField testid="tt-bg" value={ts.bg} fallback="#ffffff" onChange={(v) => set({ bg: v })} /></Row>
          <Row label="Text color"><ColorField testid="tt-color" value={ts.color} fallback="#0A1029" onChange={(v) => set({ color: v })} /></Row>
          <Row label="Border color"><ColorField testid="tt-border" value={ts.borderColor} fallback="#2B4EFF" onChange={(v) => set({ borderColor: v })} /></Row>
          <Row label={`Font size (${ts.fontSize || 12}px)`}>
            <div className="w-[110px]"><Slider data-testid="tt-fontsize" min={9} max={20} step={1} value={[Number(ts.fontSize) || 12]} onValueChange={([v]) => set({ fontSize: v })} /></div>
          </Row>
          <Row label={`Corner radius (${ts.radius ?? 14}px)`}>
            <div className="w-[110px]"><Slider data-testid="tt-radius" min={0} max={24} step={2} value={[ts.radius ?? 14]} onValueChange={([v]) => set({ radius: v })} /></div>
          </Row>
          <Row label={`Opacity (${Math.round((ts.opacity ?? 1) * 100)}%)`}>
            <div className="w-[110px]"><Slider data-testid="tt-opacity" min={40} max={100} step={5} value={[Math.round((ts.opacity ?? 1) * 100)]} onValueChange={([v]) => set({ opacity: v / 100 })} /></div>
          </Row>
        </>
      )}
      {Object.keys(ts).length > 0 && (
        <Button data-testid="tt-reset" size="sm" variant="ghost" className="h-6 text-[color:var(--ink-3)] w-full" onClick={() => patchOpts({ tooltipStyle: {} })}>Reset tooltip style</Button>
      )}
    </div>
  );
}

// ---------- Item 180 v2: value-range colors (gradients, opacity, priority, legend) ----------
export function RangeColorsEditor({ opts, patchOpts }) {
  const ranges = opts.rangeColors || [];
  const set = (arr) => patchOpts({ rangeColors: arr });
  const upd = (i, patch) => set(ranges.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= ranges.length) return;
    const arr = [...ranges];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    set(arr);
  };
  return (
    <div data-testid="range-colors-editor" className="space-y-1.5 rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-3">
      <div className="flex items-center justify-between">
        <Label className="text-xs text-[color:var(--ink-2)] flex items-center gap-1.5"><Paintbrush className="w-3.5 h-3.5" />Range colors</Label>
        <Button data-testid="range-add" size="sm" variant="ghost" className="h-6 text-[color:var(--brand-ocean)]"
          onClick={() => set([...ranges, { start: "", end: "", colors: ["#E24B5B"], label: "", gradientType: "", opacity: 1, enabled: true }])}><Plus className="w-3.5 h-3.5" /></Button>
      </div>
      {ranges.map((r, i) => (
        <div key={i} className={"rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-2 space-y-1" + (r.enabled === false ? " opacity-50" : "")}>
          <div className="flex gap-1 items-center">
            <Input data-testid={`range-start-${i}`} type="number" value={r.start ?? ""} onChange={(e) => upd(i, { start: e.target.value })} placeholder="From" className="bg-[color:var(--surface-2)] h-7 text-xs flex-1 min-w-0" />
            <span className="text-[10px] text-[color:var(--ink-3)]">–</span>
            <Input data-testid={`range-end-${i}`} type="number" value={r.end ?? ""} onChange={(e) => upd(i, { end: e.target.value })} placeholder="To" className="bg-[color:var(--surface-2)] h-7 text-xs flex-1 min-w-0" />
            <input data-testid={`range-color-${i}`} type="color" value={(r.colors || [])[0] || "#E24B5B"}
              onChange={(e) => upd(i, { colors: [e.target.value, ...(r.colors || []).slice(1)] })}
              className="h-7 w-8 rounded-lg border border-[color:var(--glass-border)] bg-transparent cursor-pointer shrink-0" />
            <input data-testid={`range-color2-${i}`} type="color" title="Optional gradient end color" value={(r.colors || [])[1] || (r.colors || [])[0] || "#E24B5B"}
              onChange={(e) => upd(i, { colors: [(r.colors || [])[0] || "#E24B5B", e.target.value] })}
              className="h-7 w-8 rounded-lg border border-dashed border-[color:var(--glass-border)] bg-transparent cursor-pointer shrink-0" />
            <button data-testid={`range-remove-${i}`} onClick={() => set(ranges.filter((_, j) => j !== i))}
              className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)] shrink-0"><X className="w-3.5 h-3.5" /></button>
          </div>
          <div className="flex gap-1 items-center">
            <Input data-testid={`range-label-${i}`} value={r.label || ""} onChange={(e) => upd(i, { label: e.target.value })} placeholder="Label (legend)" className="bg-[color:var(--surface-2)] h-6 text-[11px] flex-1 min-w-0" />
            <Select value={r.gradientType || "blend"} onValueChange={(v) => upd(i, { gradientType: v === "blend" ? "" : v })}>
              <SelectTrigger data-testid={`range-gradient-${i}`} className="bg-[color:var(--surface-2)] w-[84px] h-6 text-[10px] shrink-0"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="blend">Blend</SelectItem>
                <SelectItem value="solid">Solid</SelectItem>
                <SelectItem value="linear">Linear</SelectItem>
                <SelectItem value="radial">Radial</SelectItem>
              </SelectContent>
            </Select>
            <Select value={r.bounds || "[]"} onValueChange={(v) => upd(i, { bounds: v })}>
              <SelectTrigger data-testid={`range-bounds-${i}`} title="Boundary inclusion: [ ] inclusive, ( ) exclusive" className="bg-[color:var(--surface-2)] w-[64px] h-6 text-[10px] shrink-0"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="[]">[a, b]</SelectItem>
                <SelectItem value="[)">[a, b)</SelectItem>
                <SelectItem value="(]">(a, b]</SelectItem>
                <SelectItem value="()">(a, b)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-1.5 items-center">
            <Label className="text-[10px] text-[color:var(--ink-3)] shrink-0">Opacity {Math.round((r.opacity ?? 1) * 100)}%</Label>
            <div className="flex-1"><Slider data-testid={`range-opacity-${i}`} min={10} max={100} step={5} value={[Math.round((r.opacity ?? 1) * 100)]} onValueChange={([v]) => upd(i, { opacity: v / 100 })} /></div>
            <button data-testid={`range-up-${i}`} title="Higher priority" disabled={i === 0} onClick={() => move(i, -1)} className="text-[color:var(--ink-3)] hover:text-[color:var(--ink)] disabled:opacity-30"><ChevronUp className="w-3.5 h-3.5" /></button>
            <button data-testid={`range-down-${i}`} title="Lower priority" disabled={i === ranges.length - 1} onClick={() => move(i, 1)} className="text-[color:var(--ink-3)] hover:text-[color:var(--ink)] disabled:opacity-30"><ChevronDown className="w-3.5 h-3.5" /></button>
            <Switch data-testid={`range-enabled-${i}`} checked={r.enabled !== false} onCheckedChange={(v) => upd(i, { enabled: v })} />
          </div>
        </div>
      ))}
      {ranges.length > 0 && (
        <>
          <Row label="Legend shows">
            <Select value={opts.rangeLegend || "auto"} onValueChange={(v) => patchOpts({ rangeLegend: v === "auto" ? "" : v })}>
              <SelectTrigger data-testid="range-legend-mode" className="bg-[color:var(--surface)] w-[130px] h-7 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="auto">Series (default)</SelectItem>
                <SelectItem value="ranges">Range labels</SelectItem>
                <SelectItem value="both">Both</SelectItem>
              </SelectContent>
            </Select>
          </Row>
          <p className="text-[10px] text-[color:var(--ink-3)]">Rules are checked top-down (first match wins). Two colors blend across the range, or pick Linear / Radial for a gradient fill per point. Applies to bars, lines, points, slices, funnel stages and treemap tiles.</p>
        </>
      )}
    </div>
  );
}

// ---------- Item 167: Text / Note widget styling ----------
export function TextStyleEditor({ config, patchConfig }) {
  const ts = config.textStyle || {};
  const set = (patch) => patchConfig({ textStyle: { ...ts, ...patch } });
  return (
    <div data-testid="text-style-editor" className="space-y-1.5 rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-3">
      <Label className="text-xs text-[color:var(--ink-2)] flex items-center gap-1.5"><TypeIcon className="w-3.5 h-3.5" />Text style</Label>
      <Row label={`Font size (${ts.size || 14}px)`}>
        <div className="w-[110px]"><Slider data-testid="text-size" min={10} max={48} step={1} value={[Number(ts.size) || 14]} onValueChange={([v]) => set({ size: v })} /></div>
      </Row>
      <Row label="Align">
        <Select value={ts.align || "left"} onValueChange={(v) => set({ align: v })}>
          <SelectTrigger data-testid="text-align" className="bg-[color:var(--surface)] w-[100px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="left">Left</SelectItem>
            <SelectItem value="center">Center</SelectItem>
            <SelectItem value="right">Right</SelectItem>
          </SelectContent>
        </Select>
      </Row>
      <Row label="Font">
        <Select value={ts.fontFamily || "default"} onValueChange={(v) => set({ fontFamily: v === "default" ? "" : v })}>
          <SelectTrigger data-testid="text-font-family" className="bg-[color:var(--surface)] w-[130px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{TEXT_FONTS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
        </Select>
      </Row>
      <Row label="Text color"><ColorField testid="text-color" value={ts.color} fallback="#232B4A" onChange={(v) => set({ color: v })} /></Row>
      <Row label="Background"><ColorField testid="text-bg" value={ts.bg} fallback="#ffffff" onChange={(v) => set({ bg: v })} /></Row>
      <Row label="Bold"><Switch data-testid="text-bold" checked={!!ts.bold} onCheckedChange={(v) => set({ bold: v })} /></Row>
      <Row label="Italic"><Switch data-testid="text-italic" checked={!!ts.italic} onCheckedChange={(v) => set({ italic: v })} /></Row>
      <Row label="Underline"><Switch data-testid="text-underline" checked={!!ts.underline} onCheckedChange={(v) => set({ underline: v })} /></Row>
      <p className="text-[10px] text-[color:var(--ink-3)]">Markdown-lite: <b># Heading</b>, <b>**bold**</b>, <b>*italic*</b> and <b>- bullets</b> are rendered.</p>
    </div>
  );
}

// ---------- Item 20: rich Text widget — per-section (Title / Content) formatting ----------
export const TEXT_FONTS = [
  { value: "default", label: "Theme default" },
  { value: "Outfit, sans-serif", label: "Outfit" },
  { value: "Manrope, sans-serif", label: "Manrope" },
  { value: "Inter, sans-serif", label: "Inter" },
  { value: "Georgia, serif", label: "Georgia" },
  { value: "'Times New Roman', serif", label: "Times" },
  { value: "'Courier New', monospace", label: "Monospace" },
  { value: "Arial, sans-serif", label: "Arial" },
];

function RichSectionBlock({ sec, label, style, set, defaultSize }) {
  return (
    <div data-testid={`richtext-style-${sec}`} className="space-y-1.5 rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-2.5">
      <Label className="text-[11px] font-semibold text-[color:var(--ink-2)]">{label}</Label>
      <Row label="Font size (px)">
        <Input data-testid={`richtext-${sec}-size`} type="number" min={8} max={72} value={style.sizePx ?? ""} placeholder={String(defaultSize)}
          onChange={(e) => set({ sizePx: e.target.value === "" ? "" : Math.max(8, Math.min(72, Number(e.target.value))) })}
          className="bg-[color:var(--surface-2)] w-[72px] h-7 text-xs" />
      </Row>
      <Row label="Font">
        <Select value={style.fontFamily || "default"} onValueChange={(v) => set({ fontFamily: v === "default" ? "" : v })}>
          <SelectTrigger data-testid={`richtext-${sec}-font`} className="bg-[color:var(--surface-2)] w-[130px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{TEXT_FONTS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
        </Select>
      </Row>
      <Row label="Align">
        <Select value={style.align || "left"} onValueChange={(v) => set({ align: v })}>
          <SelectTrigger data-testid={`richtext-${sec}-align`} className="bg-[color:var(--surface-2)] w-[100px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="left">Left</SelectItem>
            <SelectItem value="center">Center</SelectItem>
            <SelectItem value="right">Right</SelectItem>
            <SelectItem value="justify">Justify</SelectItem>
          </SelectContent>
        </Select>
      </Row>
      <Row label="Text color"><ColorField testid={`richtext-${sec}-color`} value={style.color} fallback="#232B4A" onChange={(v) => set({ color: v })} /></Row>
      <Row label="Background"><ColorField testid={`richtext-${sec}-bg`} value={style.bg} fallback="#ffffff" onChange={(v) => set({ bg: v })} /></Row>
      <Row label="Bold"><Switch data-testid={`richtext-${sec}-bold`} checked={!!style.bold} onCheckedChange={(v) => set({ bold: v })} /></Row>
      <Row label="Italic"><Switch data-testid={`richtext-${sec}-italic`} checked={!!style.italic} onCheckedChange={(v) => set({ italic: v })} /></Row>
      <Row label="Underline"><Switch data-testid={`richtext-${sec}-underline`} checked={!!style.underline} onCheckedChange={(v) => set({ underline: v })} /></Row>
    </div>
  );
}

export function RichTextSectionsEditor({ config, patchConfig }) {
  const rich = config.rich || {};
  const setSec = (sec) => (patch) => patchConfig({ rich: { ...rich, [sec]: { ...(rich[sec] || {}), ...patch } } });
  return (
    <div data-testid="richtext-style-editor" className="space-y-2 rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-3">
      <Label className="text-xs text-[color:var(--ink-2)] flex items-center gap-1.5"><TypeIcon className="w-3.5 h-3.5" />Rich text sections</Label>
      <RichSectionBlock sec="title" label="Title" style={rich.title || {}} set={setSec("title")} defaultSize={18} />
      <RichSectionBlock sec="content" label="Content" style={rich.content || {}} set={setSec("content")} defaultSize={14} />
      <p className="text-[10px] text-[color:var(--ink-3)]">Section styles apply to the whole Title / Content block. Select text on the canvas and use the floating toolbar to format just that selection.</p>
    </div>
  );
}

// ---------- Item 166 + Batch 9: enterprise Image widget properties ----------
export function ImageWidgetEditor({ config, patchConfig, datasets = [] }) {
  const img = config.image || {};
  const set = (patch) => patchConfig({ image: { ...img, ...patch } });
  const source = img.source === "field" ? "field" : "static";
  const ds = datasets.find((d) => d.id === img.dataset_id);
  const fields = (ds?.schema || []).map((c) => c.name);
  const action = img.action || (img.link ? { type: "url", url: img.link } : { type: "none" });
  const setAction = (p) => set({ action: { ...action, ...p }, link: null });
  const [dashboards, setDashboards] = React.useState([]);
  React.useEffect(() => {
    if (action.type === "dashboard" && dashboards.length === 0) {
      api.get("/dashboards").then(({ data }) => setDashboards(data)).catch(() => {});
    }
  }, [action.type]); // eslint-disable-line react-hooks/exhaustive-deps
  const zoom = Math.max(100, Number(img.zoom) || 100);
  const showFocal = zoom > 100 || (img.fit || "contain") === "cover";
  return (
    <div data-testid="image-widget-editor" className="space-y-2 rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-3">
      <Label className="text-xs text-[color:var(--ink-2)] flex items-center gap-1.5"><ImageIcon className="w-3.5 h-3.5" />Image</Label>
      <Row label="Source">
        <Select value={source} onValueChange={(v) => set({ source: v === "field" ? "field" : null })}>
          <SelectTrigger data-testid="image-source" className="bg-[color:var(--surface)] w-[130px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="static">Static URL</SelectItem>
            <SelectItem value="field">Dataset field</SelectItem>
          </SelectContent>
        </Select>
      </Row>
      {source === "static" ? (
        <div>
          <Label className="text-[11px] text-[color:var(--ink-3)]">Image URL or upload</Label>
          <div className="mt-1"><MediaInput testid="image-url" value={img.url || ""} onChange={(v) => set({ url: v })} /></div>
        </div>
      ) : (
        <>
          <div>
            <Label className="text-[11px] text-[color:var(--ink-3)]">Dataset</Label>
            <Select value={img.dataset_id || ""} onValueChange={(v) => set({ dataset_id: v, field: null })}>
              <SelectTrigger data-testid="image-dataset" className="mt-1 bg-[color:var(--surface)] h-7 text-xs"><SelectValue placeholder="Pick dataset" /></SelectTrigger>
              <SelectContent>{datasets.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] text-[color:var(--ink-3)]">Image URL field</Label>
            <Select value={img.field || ""} onValueChange={(v) => set({ field: v })} disabled={!img.dataset_id}>
              <SelectTrigger data-testid="image-field" className="mt-1 bg-[color:var(--surface)] h-7 text-xs"><SelectValue placeholder="Pick field" /></SelectTrigger>
              <SelectContent>{fields.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-[10px] text-[color:var(--ink-3)] mt-1">The first value of this field (after dashboard filters) is shown — e.g. a product photo that follows the product filter.</p>
          </div>
        </>
      )}
      <Row label="Fit">
        <Select value={img.fit || "contain"} onValueChange={(v) => set({ fit: v })}>
          <SelectTrigger data-testid="image-fit" className="bg-[color:var(--surface)] w-[110px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="contain">Fit inside</SelectItem>
            <SelectItem value="cover">Fill (crop)</SelectItem>
            <SelectItem value="fill">Stretch</SelectItem>
            <SelectItem value="none">Original size</SelectItem>
          </SelectContent>
        </Select>
      </Row>
      <Row label={`Zoom (${zoom}%)`}>
        <div className="w-[110px]"><Slider data-testid="image-zoom" min={100} max={300} step={5} value={[zoom]} onValueChange={([v]) => set({ zoom: v })} /></div>
      </Row>
      {showFocal && (
        <>
          <Row label={`Focus X (${img.posX ?? 50}%)`}>
            <div className="w-[110px]"><Slider data-testid="image-posx" min={0} max={100} step={5} value={[img.posX ?? 50]} onValueChange={([v]) => set({ posX: v })} /></div>
          </Row>
          <Row label={`Focus Y (${img.posY ?? 50}%)`}>
            <div className="w-[110px]"><Slider data-testid="image-posy" min={0} max={100} step={5} value={[img.posY ?? 50]} onValueChange={([v]) => set({ posY: v })} /></div>
          </Row>
        </>
      )}
      <Row label="Hover effect">
        <Select value={img.hover || "none"} onValueChange={(v) => set({ hover: v })}>
          <SelectTrigger data-testid="image-hover" className="bg-[color:var(--surface)] w-[130px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">None</SelectItem>
            <SelectItem value="zoom">Zoom in</SelectItem>
            <SelectItem value="lift">Lift (shadow)</SelectItem>
            <SelectItem value="fade">Fade in</SelectItem>
            <SelectItem value="grayscale">Gray → color</SelectItem>
          </SelectContent>
        </Select>
      </Row>
      <Row label={`Opacity (${Math.round((img.opacity ?? 1) * 100)}%)`}>
        <div className="w-[110px]"><Slider data-testid="image-opacity" min={10} max={100} step={5} value={[Math.round((img.opacity ?? 1) * 100)]} onValueChange={([v]) => set({ opacity: v / 100 })} /></div>
      </Row>
      <Row label={`Corner radius (${img.radius ?? 0}px)`}>
        <div className="w-[110px]"><Slider data-testid="image-radius" min={0} max={32} step={2} value={[img.radius ?? 0]} onValueChange={([v]) => set({ radius: v })} /></div>
      </Row>
      <div className="pt-1 border-t border-[color:var(--glass-border)]">
        <Label className="text-[11px] text-[color:var(--ink-3)]">On click</Label>
        <Select value={action.type || "none"} onValueChange={(v) => setAction({ type: v })}>
          <SelectTrigger data-testid="image-action-type" className="mt-1 bg-[color:var(--surface)] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="none">Do nothing</SelectItem>
            <SelectItem value="url">Open URL</SelectItem>
            <SelectItem value="dashboard">Go to dashboard</SelectItem>
          </SelectContent>
        </Select>
        {action.type === "url" && (
          <Input data-testid="image-action-url" value={action.url || ""} onChange={(e) => setAction({ url: e.target.value })} placeholder="https://…" className="mt-1.5 bg-[color:var(--surface)] h-7 text-xs" />
        )}
        {action.type === "dashboard" && (
          <Select value={action.dashboard_id || ""} onValueChange={(v) => setAction({ dashboard_id: v })}>
            <SelectTrigger data-testid="image-action-dashboard" className="mt-1.5 bg-[color:var(--surface)] h-7 text-xs"><SelectValue placeholder="Pick target dashboard" /></SelectTrigger>
            <SelectContent>{dashboards.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>)}</SelectContent>
          </Select>
        )}
      </div>
      <div>
        <Label className="text-[11px] text-[color:var(--ink-3)]">Alt text</Label>
        <Input data-testid="image-alt" value={img.alt || ""} onChange={(e) => set({ alt: e.target.value })} placeholder="Describe the image" className="mt-1 bg-[color:var(--surface)] h-7 text-xs" />
      </div>
    </div>
  );
}

// ---------- Item 165 v2: Multi-filter widget fields + behavior ----------
export function MultiFilterEditor({ config, patchConfig, dims, disp }) {
  const fields = config.fields || [];
  const set = (arr) => patchConfig({ fields: arr });
  const opts = config.options || {};
  const setOpts = (patch) => patchConfig({ options: { ...opts, ...patch } });
  return (
    <div data-testid="multifilter-editor" className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label className="text-xs text-[color:var(--ink-3)] flex items-center gap-1"><SlidersHorizontal className="w-3.5 h-3.5" />Filter fields</Label>
        <Button data-testid="multifilter-add-field" size="sm" variant="ghost" className="h-6 text-[color:var(--brand-ocean)]"
          onClick={() => set([...fields, { field: dims[0]?.name || "", label: "", multi: true }])}><Plus className="w-3.5 h-3.5" /></Button>
      </div>
      {fields.map((f, i) => (
        <div key={i} className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-1.5 space-y-1">
          <div className="flex gap-1 items-center">
            <Select value={f.field || ""} onValueChange={(v) => set(fields.map((x, j) => (j === i ? { ...x, field: v } : x)))}>
              <SelectTrigger data-testid={`multifilter-field-${i}`} className="bg-[color:var(--surface-2)] flex-1 min-w-0 h-8 text-xs"><SelectValue placeholder="Field" /></SelectTrigger>
              <SelectContent>{dims.map((c) => <SelectItem key={c.name} value={c.name}>{disp(c.name)}</SelectItem>)}</SelectContent>
            </Select>
            <Input data-testid={`multifilter-label-${i}`} value={f.label || ""} onChange={(e) => set(fields.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)))}
              placeholder="Label" className="bg-[color:var(--surface-2)] w-[92px] h-8 text-xs shrink-0" />
            <button onClick={() => set(fields.filter((_, j) => j !== i))} className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)] shrink-0"><X className="w-3.5 h-3.5" /></button>
          </div>
          <div className="flex gap-2 items-center">
            <label className="flex items-center gap-1 text-[10px] text-[color:var(--ink-3)]">
              <Switch data-testid={`multifilter-multi-${i}`} checked={!!f.multi} onCheckedChange={(v) => set(fields.map((x, j) => (j === i ? { ...x, multi: v } : x)))} />Multi-select
            </label>
            <Input data-testid={`multifilter-default-${i}`} value={f.default || ""} onChange={(e) => set(fields.map((x, j) => (j === i ? { ...x, default: e.target.value } : x)))}
              placeholder="Default (comma-sep)" className="bg-[color:var(--surface-2)] flex-1 h-6 text-[10px]" />
          </div>
        </div>
      ))}
      <div className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-2 space-y-1.5">
        <Label className="text-[10px] font-semibold text-[color:var(--ink-3)] uppercase tracking-wide">Behavior</Label>
        <Row label="Compact mode">
          <Select value={opts.compactMode || "auto"} onValueChange={(v) => setOpts({ compactMode: v })}>
            <SelectTrigger data-testid="multifilter-compact-mode" className="bg-[color:var(--surface-2)] w-[110px] h-7 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Auto (by size)</SelectItem>
              <SelectItem value="on">Always</SelectItem>
              <SelectItem value="off">Never</SelectItem>
            </SelectContent>
          </Select>
        </Row>
        {(opts.compactMode || "auto") !== "off" && (
          <Row label="Button label">
            <Input data-testid="multifilter-compact-label" value={opts.compactLabel || ""} placeholder="Filters"
              onChange={(e) => setOpts({ compactLabel: e.target.value })} className="bg-[color:var(--surface-2)] w-[110px] h-7 text-xs" />
          </Row>
        )}
        <Row label="Apply mode">
          <Select value={opts.applyMode || "instant"} onValueChange={(v) => setOpts({ applyMode: v })}>
            <SelectTrigger data-testid="multifilter-apply-mode" className="bg-[color:var(--surface-2)] w-[110px] h-7 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="instant">Instant</SelectItem>
              <SelectItem value="apply">Apply button</SelectItem>
            </SelectContent>
          </Select>
        </Row>
        {opts.applyMode === "apply" && (
          <Row label="Close popup on apply"><Switch data-testid="multifilter-close-on-apply" checked={opts.closeOnApply !== false} onCheckedChange={(v) => setOpts({ closeOnApply: v })} /></Row>
        )}
        <Row label="Max chips shown">
          <Select value={String(opts.maxChips || 2)} onValueChange={(v) => setOpts({ maxChips: Number(v) })}>
            <SelectTrigger data-testid="multifilter-max-chips" className="bg-[color:var(--surface-2)] w-[70px] h-7 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>{[1, 2, 3, 4, 5].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
          </Select>
        </Row>
      </div>
      <p className="text-[10px] text-[color:var(--ink-3)]">Selections cascade to the other filters and cross-filter every widget on the same dataset. Multi-select uses checkboxes with search, Select all and Clear.</p>
    </div>
  );
}

// ---------- Item 174 v2: Data grid options + per-column configuration ----------
const GRID_AGGS = [["none", "None"], ["sum", "Sum"], ["avg", "Average"], ["min", "Min"], ["max", "Max"], ["count", "Count"], ["countd", "Distinct"]];
const GRID_FMTS = [["none", "Raw"], ["number", "Number"], ["currency", "Currency"], ["percent", "Percent"], ["date", "Date"], ["time", "Time"], ["datetime", "Date + time"]];
const GRID_RULE_OPS = [["gt", ">"], ["gte", "≥"], ["lt", "<"], ["lte", "≤"], ["between", "between"], ["eq", "="], ["neq", "≠"], ["contains", "contains"], ["starts", "starts with"], ["ends", "ends with"], ["blank", "blank"], ["nblank", "not blank"], ["top_n", "Top N"], ["bottom_n", "Bottom N"], ["above_avg", "above avg"], ["below_avg", "below avg"]];
const GRID_RULE_ICONS = [["", "No icon"], ["arrow-up", "▲ Arrow up"], ["arrow-down", "▼ Arrow down"], ["circle", "● Circle"], ["flag", "⚑ Flag"], ["star", "★ Star"], ["check", "✓ Check"], ["cross", "✗ Cross"], ["triangle", "▲ Triangle"], ["diamond", "◆ Diamond"]];
export function DataGridEditor({ config, opts, patchConfig, schema, disp }) {
  const grid = config.grid || {};
  const set = (patch) => patchConfig({ grid: { ...grid, ...patch } });
  const colCfg = grid.colCfg || {};
  const setCol = (name, patch) => set({ colCfg: { ...colCfg, [name]: { ...(colCfg[name] || {}), ...patch } } });
  const cols = config.columns || [];
  const allNames = (schema || []).map((c) => c.name);
  const ordered = cols.length ? cols : allNames;
  const toggleCol = (name) => {
    const base = cols.length ? cols : allNames;
    const has = base.includes(name);
    patchConfig({ columns: has ? base.filter((c) => c !== name) : [...base, name] });
  };
  const moveCol = (name, d) => {
    const base = [...(cols.length ? cols : allNames)];
    const i = base.indexOf(name);
    const j = i + d;
    if (i < 0 || j < 0 || j >= base.length) return;
    [base[i], base[j]] = [base[j], base[i]];
    patchConfig({ columns: base });
  };
  const [openCol, setOpenCol] = React.useState(null);
  const hdr = grid.header || {};
  const setHdr = (patch) => set({ header: { ...hdr, ...patch } });
  const dimNames = (schema || []).filter((c) => !["int", "float", "number"].includes(String(c.type || "").toLowerCase())).map((c) => c.name);
  return (
    <div data-testid="datagrid-editor" className="space-y-2">
      <Label className="text-[11px] text-[color:var(--ink-2)] font-medium flex items-center gap-1"><Grid3x3 className="w-3 h-3" />Grid options</Label>
      <Row label="Page size">
        <Select value={String(grid.pageSize || 25)} onValueChange={(v) => set({ pageSize: Number(v) })}>
          <SelectTrigger data-testid="grid-page-size" className="bg-[color:var(--surface)] w-[90px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{[10, 25, 50, 100, 250, 500].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
        </Select>
      </Row>
      <Row label="Search box"><Switch data-testid="grid-show-search" checked={grid.showSearch !== false} onCheckedChange={(v) => set({ showSearch: v })} /></Row>
      <Row label="Column filters"><Switch data-testid="grid-col-filters" checked={grid.colFilters !== false} onCheckedChange={(v) => set({ colFilters: v })} /></Row>
      <Row label="Row selection"><Switch data-testid="grid-selectable" checked={!!grid.selectable} onCheckedChange={(v) => set({ selectable: v })} /></Row>
      <Row label="Striped rows"><Switch data-testid="grid-striped" checked={!!grid.striped} onCheckedChange={(v) => set({ striped: v })} /></Row>
      <Row label="Compact density"><Switch data-testid="grid-dense" checked={!!grid.dense} onCheckedChange={(v) => set({ dense: v })} /></Row>
      <Row label="Totals footer"><Switch data-testid="grid-footer" checked={grid.showFooter !== false} onCheckedChange={(v) => set({ showFooter: v })} /></Row>
      <Row label="Cell text">
        <Select value={grid.wrap || "nowrap"} onValueChange={(v) => set({ wrap: v })}>
          <SelectTrigger data-testid="grid-wrap" className="bg-[color:var(--surface)] w-[110px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="nowrap">Ellipsis</SelectItem>
            <SelectItem value="wrap">Wrap</SelectItem>
          </SelectContent>
        </Select>
      </Row>
      <Row label="Freeze left columns">
        <Select value={String(grid.frozen || 0)} onValueChange={(v) => set({ frozen: Number(v) })}>
          <SelectTrigger data-testid="grid-frozen" className="bg-[color:var(--surface)] w-[70px] h-7 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent>{[0, 1, 2, 3].map((n) => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
        </Select>
      </Row>
      <Row label={`Row height (${grid.rowHeight || (grid.dense ? 28 : 34)}px)`}>
        <div className="w-[100px]"><Slider data-testid="grid-row-height" min={22} max={64} step={2} value={[grid.rowHeight || (grid.dense ? 28 : 34)]} onValueChange={([v]) => set({ rowHeight: v })} /></div>
      </Row>
      <div>
        <Label className="text-[11px] text-[color:var(--ink-3)]">Group by (drag order = hierarchy)</Label>
        <div className="mt-1 space-y-1">
          {(grid.groupBy || []).map((g, i) => (
            <div key={i} className="flex gap-1 items-center">
              <Select value={g || ""} onValueChange={(v) => set({ groupBy: (grid.groupBy || []).map((x, j) => (j === i ? v : x)) })}>
                <SelectTrigger data-testid={`grid-group-${i}`} className="bg-[color:var(--surface)] flex-1 h-7 text-xs"><SelectValue placeholder="Column" /></SelectTrigger>
                <SelectContent>{dimNames.map((n) => <SelectItem key={n} value={n}>{disp ? disp(n) : n}</SelectItem>)}</SelectContent>
              </Select>
              <button onClick={() => set({ groupBy: (grid.groupBy || []).filter((_, j) => j !== i) })} className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)]"><X className="w-3.5 h-3.5" /></button>
            </div>
          ))}
          {(grid.groupBy || []).length < 2 && (
            <Button data-testid="grid-group-add" size="sm" variant="outline" className="w-full h-6 text-[10px]" onClick={() => set({ groupBy: [...(grid.groupBy || []), ""] })}><Plus className="w-3 h-3 mr-1" />Add grouping level</Button>
          )}
        </div>
      </div>
      <div className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-2 space-y-1.5">
        <Label className="text-[10px] font-semibold text-[color:var(--ink-3)] uppercase tracking-wide">Header style</Label>
        <div className="flex gap-1.5 items-center flex-wrap">
          <input data-testid="grid-header-bg" type="color" title="Background" value={hdr.bg || "#f4f6fb"} onChange={(e) => setHdr({ bg: e.target.value })} className="h-7 w-8 rounded-lg border border-[color:var(--glass-border)] bg-transparent cursor-pointer" />
          <input data-testid="grid-header-color" type="color" title="Text color" value={hdr.color || "#3d4966"} onChange={(e) => setHdr({ color: e.target.value })} className="h-7 w-8 rounded-lg border border-[color:var(--glass-border)] bg-transparent cursor-pointer" />
          <Input data-testid="grid-header-size" type="number" min={9} max={20} value={hdr.fontSize ?? ""} placeholder="px" onChange={(e) => setHdr({ fontSize: e.target.value === "" ? "" : Number(e.target.value) })} className="bg-[color:var(--surface-2)] w-[64px] h-7 text-xs" />
          <label className="flex items-center gap-1 text-[10px] text-[color:var(--ink-3)]"><Switch checked={hdr.weight === "bold"} onCheckedChange={(v) => setHdr({ weight: v ? "bold" : "" })} />Bold</label>
          <label className="flex items-center gap-1 text-[10px] text-[color:var(--ink-3)]"><Switch data-testid="grid-header-italic" checked={!!hdr.italic} onCheckedChange={(v) => setHdr({ italic: v })} />Italic</label>
          <label className="flex items-center gap-1 text-[10px] text-[color:var(--ink-3)]"><Switch data-testid="grid-header-underline" checked={!!hdr.underline} onCheckedChange={(v) => setHdr({ underline: v })} />Underline</label>
          {(hdr.bg || hdr.color) && <button onClick={() => set({ header: {} })} className="text-[color:var(--ink-3)] hover:text-[color:var(--ink)]"><X className="w-3 h-3" /></button>}
        </div>
      </div>
      <div className="rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-2 space-y-1.5">
        <Label className="text-[10px] font-semibold text-[color:var(--ink-3)] uppercase tracking-wide">Content style</Label>
        <div className="flex gap-1.5 items-center flex-wrap">
          <input data-testid="grid-content-bg" type="color" title="Row background" value={(grid.content || {}).bg || "#ffffff"} onChange={(e) => set({ content: { ...(grid.content || {}), bg: e.target.value } })} className="h-7 w-8 rounded-lg border border-[color:var(--glass-border)] bg-transparent cursor-pointer" />
          <input data-testid="grid-content-color" type="color" title="Text color" value={(grid.content || {}).color || "#3d4966"} onChange={(e) => set({ content: { ...(grid.content || {}), color: e.target.value } })} className="h-7 w-8 rounded-lg border border-[color:var(--glass-border)] bg-transparent cursor-pointer" />
          <Input data-testid="grid-content-size" type="number" min={9} max={20} value={(grid.content || {}).fontSize ?? ""} placeholder="px" onChange={(e) => set({ content: { ...(grid.content || {}), fontSize: e.target.value === "" ? "" : Number(e.target.value) } })} className="bg-[color:var(--surface-2)] w-[64px] h-7 text-xs" />
          {(grid.content && (grid.content.bg || grid.content.color)) && <button onClick={() => set({ content: {} })} className="text-[color:var(--ink-3)] hover:text-[color:var(--ink)]"><X className="w-3 h-3" /></button>}
        </div>
        <div className="flex gap-1.5 items-center flex-wrap">
          <span className="text-[10px] text-[color:var(--ink-3)]">Alt rows</span>
          <input data-testid="grid-alt-bg" type="color" title="Alternate row background" value={grid.altBg || "#f6f8fc"} onChange={(e) => set({ altBg: e.target.value })} className="h-7 w-8 rounded-lg border border-[color:var(--glass-border)] bg-transparent cursor-pointer" />
          <input data-testid="grid-alt-color" type="color" title="Alternate row text" value={grid.altColor || "#3d4966"} onChange={(e) => set({ altColor: e.target.value })} className="h-7 w-8 rounded-lg border border-[color:var(--glass-border)] bg-transparent cursor-pointer" />
          {(grid.altBg || grid.altColor) && <button data-testid="grid-alt-clear" onClick={() => set({ altBg: "", altColor: "" })} className="text-[color:var(--ink-3)] hover:text-[color:var(--ink)]"><X className="w-3 h-3" /></button>}
          <label className="flex items-center gap-1 text-[10px] text-[color:var(--ink-3)] ml-2"><Switch data-testid="grid-vlines" checked={!!(grid.lines || {}).v} onCheckedChange={(v) => set({ lines: { ...(grid.lines || {}), v } })} />V lines</label>
          <input data-testid="grid-line-color" type="color" title="Grid line color" value={(grid.lines || {}).color || "#dde3f0"} onChange={(e) => set({ lines: { ...(grid.lines || {}), color: e.target.value } })} className="h-7 w-8 rounded-lg border border-[color:var(--glass-border)] bg-transparent cursor-pointer" />
        </div>
      </div>
      <div>
        <Label className="text-[11px] text-[color:var(--ink-3)]">Columns ({cols.length ? `${cols.length} shown` : "all"}) — click a column to configure</Label>
        <div className="mt-1 max-h-64 overflow-auto rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-1.5 space-y-0.5">
          {ordered.filter((n) => allNames.includes(n)).map((name, i) => {
            const visible = !cols.length || cols.includes(name);
            const cc = colCfg[name] || {};
            const isOpen = openCol === name;
            return (
              <div key={name} className="rounded-md">
                <div className="flex items-center gap-1 px-1 py-0.5 hover:bg-[color:var(--surface-2)] rounded">
                  <input data-testid={`grid-col-${name}`} type="checkbox" checked={visible} onChange={() => toggleCol(name)} className="accent-[#2B4EFF]" />
                  <button onClick={() => setOpenCol(isOpen ? null : name)} className="flex-1 min-w-0 text-left text-[11px] text-[color:var(--ink-2)] truncate hover:text-[color:var(--brand-ocean)]">
                    {cc.label ? `${cc.label} (${name})` : (disp ? disp(name) : name)}
                  </button>
                  <button data-testid={`grid-col-up-${name}`} disabled={i === 0} onClick={() => moveCol(name, -1)} className="text-[color:var(--ink-3)] disabled:opacity-20 hover:text-[color:var(--ink)]"><ChevronUp className="w-3 h-3" /></button>
                  <button data-testid={`grid-col-down-${name}`} disabled={i === ordered.length - 1} onClick={() => moveCol(name, 1)} className="text-[color:var(--ink-3)] disabled:opacity-20 hover:text-[color:var(--ink)]"><ChevronDown className="w-3 h-3" /></button>
                </div>
                {isOpen && (
                  <div data-testid={`grid-col-config-${name}`} className="ml-4 mr-1 my-1 p-2 rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] space-y-1.5">
                    <div className="grid grid-cols-2 gap-1">
                      <Input data-testid={`grid-col-rename-${name}`} value={cc.label || ""} placeholder="Rename header" onChange={(e) => setCol(name, { label: e.target.value })} className="bg-[color:var(--surface)] h-7 text-xs" />
                      <Input data-testid={`grid-col-width-${name}`} type="number" min={60} max={600} value={cc.width ?? ""} placeholder="Width px" onChange={(e) => setCol(name, { width: e.target.value === "" ? "" : Number(e.target.value) })} className="bg-[color:var(--surface)] h-7 text-xs" />
                    </div>
                    <div className="grid grid-cols-2 gap-1">
                      <Select value={cc.align || "auto"} onValueChange={(v) => setCol(name, { align: v === "auto" ? "" : v })}>
                        <SelectTrigger data-testid={`grid-col-align-${name}`} className="bg-[color:var(--surface)] h-7 text-xs"><SelectValue placeholder="Align" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="auto">Auto align</SelectItem>
                          <SelectItem value="left">Left</SelectItem>
                          <SelectItem value="center">Center</SelectItem>
                          <SelectItem value="right">Right</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={cc.agg || "none"} onValueChange={(v) => setCol(name, { agg: v })}>
                        <SelectTrigger data-testid={`grid-col-agg-${name}`} className="bg-[color:var(--surface)] h-7 text-xs"><SelectValue placeholder="Aggregate" /></SelectTrigger>
                        <SelectContent>{GRID_AGGS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="grid grid-cols-2 gap-1">
                      <Select value={cc.format?.type || "none"} onValueChange={(v) => setCol(name, { format: v === "none" ? null : { ...(cc.format || {}), type: v } })}>
                        <SelectTrigger data-testid={`grid-col-format-${name}`} className="bg-[color:var(--surface)] h-7 text-xs"><SelectValue placeholder="Format" /></SelectTrigger>
                        <SelectContent>{GRID_FMTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                      </Select>
                      {cc.format?.type && (
                        <div className="flex gap-1 items-center">
                          <Input type="number" min={0} max={6} value={cc.format?.decimals ?? 0} title="Decimals" onChange={(e) => setCol(name, { format: { ...(cc.format || {}), decimals: Number(e.target.value) } })} className="bg-[color:var(--surface)] h-7 text-xs w-[52px]" />
                          <label className="flex items-center gap-1 text-[9px] text-[color:var(--ink-3)]"><Switch checked={!!cc.format?.compact} onCheckedChange={(v) => setCol(name, { format: { ...(cc.format || {}), compact: v } })} />K/M</label>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <label className="flex items-center gap-1 text-[10px] text-[color:var(--ink-3)]">
                        <Switch data-testid={`grid-col-nosort-${name}`} checked={!cc.noSort} onCheckedChange={(v) => setCol(name, { noSort: !v })} />Sort
                      </label>
                      <label className="flex items-center gap-1 text-[10px] text-[color:var(--ink-3)]">
                        <Switch data-testid={`grid-col-nofilter-${name}`} checked={!cc.noFilter} onCheckedChange={(v) => setCol(name, { noFilter: !v })} />Filter
                      </label>
                      <Select value={cc.wrap || "inherit"} onValueChange={(v) => setCol(name, { wrap: v === "inherit" ? "" : v })}>
                        <SelectTrigger data-testid={`grid-col-wrap-${name}`} className="bg-[color:var(--surface)] h-6 text-[10px] w-[86px]"><SelectValue placeholder="Wrap" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="inherit">Wrap: grid</SelectItem>
                          <SelectItem value="wrap">Wrap</SelectItem>
                          <SelectItem value="nowrap">Trim…</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-1 text-[10px] text-[color:var(--ink-3)]">
                        <Switch data-testid={`grid-col-bars-${name}`} checked={!!cc.bars} onCheckedChange={(v) => setCol(name, { bars: v ? { color: "#2B4EFF" } : null })} />Data bars
                      </label>
                      {cc.bars && <input type="color" value={cc.bars.color || "#2B4EFF"} onChange={(e) => setCol(name, { bars: { color: e.target.value } })} className="h-6 w-7 rounded border border-[color:var(--glass-border)] bg-transparent cursor-pointer" />}
                      <label className="flex items-center gap-1 text-[10px] text-[color:var(--ink-3)]">
                        <Switch data-testid={`grid-col-scale-${name}`} checked={!!cc.scale} onCheckedChange={(v) => setCol(name, { scale: v ? { min: "#FDE68A", max: "#34D399" } : null })} />Color scale
                      </label>
                      {cc.scale && (
                        <>
                          <input type="color" value={cc.scale.min || "#FDE68A"} onChange={(e) => setCol(name, { scale: { ...cc.scale, min: e.target.value } })} className="h-6 w-7 rounded border border-[color:var(--glass-border)] bg-transparent cursor-pointer" />
                          <input type="color" value={cc.scale.max || "#34D399"} onChange={(e) => setCol(name, { scale: { ...cc.scale, max: e.target.value } })} className="h-6 w-7 rounded border border-[color:var(--glass-border)] bg-transparent cursor-pointer" />
                        </>
                      )}
                    </div>
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] text-[color:var(--ink-3)]">Formatting rules</span>
                        <Button data-testid={`grid-col-rule-add-${name}`} size="sm" variant="ghost" className="h-5 text-[color:var(--brand-ocean)] px-1"
                          onClick={() => setCol(name, { rules: [...(cc.rules || []), { op: "gt", value: "", bg: "#FEE2E2" }] })}><Plus className="w-3 h-3" /></Button>
                      </div>
                      {(cc.rules || []).map((r, ri) => {
                        const updR = (patch) => setCol(name, { rules: cc.rules.map((x, j) => j === ri ? { ...x, ...patch } : x) });
                        return (
                        <div key={ri} className="mt-1 space-y-1 rounded border border-[color:var(--glass-border)] p-1">
                          <div className="flex gap-1 items-center">
                            <select value={r.op} onChange={(e) => updR({ op: e.target.value })}
                              className="h-6 text-[10px] rounded border border-[color:var(--glass-border)] bg-[color:var(--surface)] px-1">
                              {GRID_RULE_OPS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                            </select>
                            {!["blank", "nblank", "above_avg", "below_avg"].includes(r.op) && <Input value={r.value ?? ""} onChange={(e) => updR({ value: e.target.value })} className="bg-[color:var(--surface)] h-6 text-[10px] flex-1 min-w-0" placeholder={["top_n", "bottom_n"].includes(r.op) ? "N" : "Value"} />}
                            {r.op === "between" && <Input value={r.value2 ?? ""} onChange={(e) => updR({ value2: e.target.value })} className="bg-[color:var(--surface)] h-6 text-[10px] flex-1 min-w-0" placeholder="and" />}
                            <input type="color" title="Cell background" value={r.bg || "#FEE2E2"} onChange={(e) => updR({ bg: e.target.value })} className="h-6 w-7 rounded border border-[color:var(--glass-border)] bg-transparent cursor-pointer" />
                            <input type="color" title="Font color" value={r.color || "#111827"} onChange={(e) => updR({ color: e.target.value })} className="h-6 w-7 rounded border border-[color:var(--glass-border)] bg-transparent cursor-pointer" />
                            <button onClick={() => setCol(name, { rules: cc.rules.filter((_, j) => j !== ri) })} className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)]"><X className="w-3 h-3" /></button>
                          </div>
                          <div className="flex gap-1 items-center flex-wrap">
                            <select data-testid={`grid-rule-scope-${name}-${ri}`} value={r.scope || "cell"} onChange={(e) => updR({ scope: e.target.value === "cell" ? "" : e.target.value })}
                              className="h-6 text-[10px] rounded border border-[color:var(--glass-border)] bg-[color:var(--surface)] px-1">
                              <option value="cell">Cell</option>
                              <option value="row">Entire row</option>
                            </select>
                            <select data-testid={`grid-rule-icon-${name}-${ri}`} value={r.icon || ""} onChange={(e) => updR({ icon: e.target.value })}
                              className="h-6 text-[10px] rounded border border-[color:var(--glass-border)] bg-[color:var(--surface)] px-1">
                              {GRID_RULE_ICONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                            </select>
                            {r.icon && (
                              <>
                                <select value={r.iconPos || "left"} onChange={(e) => updR({ iconPos: e.target.value })}
                                  className="h-6 text-[10px] rounded border border-[color:var(--glass-border)] bg-[color:var(--surface)] px-1">
                                  <option value="left">Icon left</option>
                                  <option value="right">Icon right</option>
                                  <option value="only">Icon only</option>
                                </select>
                                <input type="color" title="Icon color" value={r.iconColor || "#EF4444"} onChange={(e) => updR({ iconColor: e.target.value })} className="h-6 w-7 rounded border border-[color:var(--glass-border)] bg-transparent cursor-pointer" />
                              </>
                            )}
                            {[["bold", "B"], ["italic", "I"], ["underline", "U"], ["strike", "S"]].map(([k, l]) => (
                              <button key={k} title={k} onClick={() => updR({ [k]: !r[k] })}
                                className={"h-6 w-6 rounded border text-[10px] " + (r[k] ? "border-[color:var(--brand-ocean)] text-[color:var(--brand-ocean)] bg-[color:rgba(43,78,255,0.08)]" : "border-[color:var(--glass-border)] text-[color:var(--ink-3)]")}>
                                {l}
                              </button>
                            ))}
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <p className="text-[10px] text-[color:var(--ink-3)] mt-1">Shift-click headers for multi-column sort. Drag the right edge of a header to resize. Use the funnel icon on a header for Excel-style filters.</p>
      </div>
    </div>
  );
}

// ---------- Batch 4: analytics overlays — reference lines + trendline ----------
export function AnalyticsEditor({ opts, patchOpts }) {
  const lines = opts.refLines || [];
  const setLines = (arr) => patchOpts({ refLines: arr });
  const upd = (i, patch) => setLines(lines.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const t = opts.trendline || {};
  const setT = (patch) => patchOpts({ trendline: { ...t, ...patch } });
  return (
    <div data-testid="analytics-editor" className="space-y-1.5 rounded-xl border border-[color:var(--glass-border)] bg-[color:var(--surface-2)] p-3">
      <div className="flex items-center justify-between">
        <Label className="text-xs text-[color:var(--ink-2)] flex items-center gap-1.5"><SlidersHorizontal className="w-3.5 h-3.5" />Analytics</Label>
        <Button data-testid="refline-add" size="sm" variant="ghost" className="h-6 text-[color:var(--brand-ocean)]"
          onClick={() => setLines([...lines, { mode: "constant", value: "", label: "", color: "#FF6B35", dash: "dashed", enabled: true }])}><Plus className="w-3.5 h-3.5" /></Button>
      </div>
      {lines.map((r, i) => (
        <div key={i} className={"rounded-lg border border-[color:var(--glass-border)] bg-[color:var(--surface)] p-2 space-y-1" + (r.enabled === false ? " opacity-50" : "")}>
          <div className="flex gap-1 items-center">
            <Select value={r.mode || "constant"} onValueChange={(v) => upd(i, { mode: v })}>
              <SelectTrigger data-testid={`refline-mode-${i}`} className="bg-[color:var(--surface-2)] flex-1 h-7 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="constant">Constant / target</SelectItem>
                <SelectItem value="avg">Average</SelectItem>
                <SelectItem value="median">Median</SelectItem>
                <SelectItem value="min">Minimum</SelectItem>
                <SelectItem value="max">Maximum</SelectItem>
              </SelectContent>
            </Select>
            {(r.mode || "constant") === "constant" && (
              <Input data-testid={`refline-value-${i}`} type="number" value={r.value ?? ""} placeholder="Value"
                onChange={(e) => upd(i, { value: e.target.value })} className="bg-[color:var(--surface-2)] w-[90px] h-7 text-xs" />
            )}
            <input data-testid={`refline-color-${i}`} type="color" value={r.color || "#FF6B35"} onChange={(e) => upd(i, { color: e.target.value })}
              className="h-7 w-8 rounded-lg border border-[color:var(--glass-border)] bg-transparent cursor-pointer shrink-0" />
            <button data-testid={`refline-remove-${i}`} onClick={() => setLines(lines.filter((_, j) => j !== i))}
              className="text-[color:var(--ink-3)] hover:text-[color:var(--danger)] shrink-0"><X className="w-3.5 h-3.5" /></button>
          </div>
          <div className="flex gap-1 items-center">
            <Input data-testid={`refline-label-${i}`} value={r.label || ""} placeholder="Label (optional)" onChange={(e) => upd(i, { label: e.target.value })} className="bg-[color:var(--surface-2)] flex-1 h-6 text-[11px]" />
            <Select value={r.dash || "dashed"} onValueChange={(v) => upd(i, { dash: v })}>
              <SelectTrigger data-testid={`refline-dash-${i}`} className="bg-[color:var(--surface-2)] w-[80px] h-6 text-[10px]"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="dashed">Dashed</SelectItem>
                <SelectItem value="solid">Solid</SelectItem>
                <SelectItem value="dotted">Dotted</SelectItem>
              </SelectContent>
            </Select>
            <Switch data-testid={`refline-enabled-${i}`} checked={r.enabled !== false} onCheckedChange={(v) => upd(i, { enabled: v })} />
          </div>
        </div>
      ))}
      <Row label="Trendline"><Switch data-testid="trendline-toggle" checked={!!t.enabled} onCheckedChange={(v) => setT({ enabled: v })} /></Row>
      <Row label="Detect anomalies"><Switch data-testid="anomalies-toggle" checked={!!opts.anomalies} onCheckedChange={(v) => patchOpts({ anomalies: v })} /></Row>
      {opts.anomalies && (
        <Row label="Sensitivity">
          <Select value={String(opts.anomalyThreshold || 2.5)} onValueChange={(v) => patchOpts({ anomalyThreshold: Number(v) })}>
            <SelectTrigger data-testid="anomaly-threshold" className="bg-[color:var(--surface)] w-[150px] h-7 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="2">High (z ≥ 2)</SelectItem>
              <SelectItem value="2.5">Normal (z ≥ 2.5)</SelectItem>
              <SelectItem value="3">Low (z ≥ 3)</SelectItem>
            </SelectContent>
          </Select>
        </Row>
      )}
      {t.enabled && (
        <>
          <Row label="Type">
            <Select value={t.type || "linear"} onValueChange={(v) => setT({ type: v })}>
              <SelectTrigger data-testid="trendline-type" className="bg-[color:var(--surface)] w-[150px] h-7 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="linear">Linear</SelectItem>
                <SelectItem value="exponential">Exponential</SelectItem>
                <SelectItem value="logarithmic">Logarithmic</SelectItem>
                <SelectItem value="polynomial">Polynomial (²)</SelectItem>
                <SelectItem value="moving_avg">Moving average</SelectItem>
              </SelectContent>
            </Select>
          </Row>
          <Row label="Apply to">
            <Select value={t.series || "first"} onValueChange={(v) => setT({ series: v })}>
              <SelectTrigger data-testid="trendline-series" className="bg-[color:var(--surface)] w-[150px] h-7 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="first">First series</SelectItem>
                <SelectItem value="all">Every series</SelectItem>
              </SelectContent>
            </Select>
          </Row>
          {t.type === "moving_avg" && (
            <Row label="Window">
              <Input data-testid="trendline-window" type="number" min={2} max={30} value={t.window ?? 3} onChange={(e) => setT({ window: Number(e.target.value) || 3 })} className="bg-[color:var(--surface)] w-[70px] h-7 text-xs" />
            </Row>
          )}
          <Row label="Style">
            <span className="flex items-center gap-1.5">
              <input data-testid="trendline-color" type="color" value={t.color || "#9AA3B8"} onChange={(e) => setT({ color: e.target.value })}
                className="h-7 w-9 rounded-lg border border-[color:var(--glass-border)] bg-transparent cursor-pointer" />
              <Input data-testid="trendline-width" type="number" min={1} max={8} placeholder="w" value={t.width ?? ""} onChange={(e) => setT({ width: e.target.value === "" ? "" : Number(e.target.value) })} className="bg-[color:var(--surface)] w-12 h-7 text-xs" />
              <Select value={t.dash || "dashed"} onValueChange={(v) => setT({ dash: v })}>
                <SelectTrigger data-testid="trendline-dash" className="bg-[color:var(--surface)] w-[86px] h-7 text-xs"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="dashed">Dashed</SelectItem>
                  <SelectItem value="solid">Solid</SelectItem>
                  <SelectItem value="dotted">Dotted</SelectItem>
                </SelectContent>
              </Select>
            </span>
          </Row>
          <Row label="Label">
            <Input data-testid="trendline-label" value={t.label || ""} placeholder="Trend" onChange={(e) => setT({ label: e.target.value })} className="bg-[color:var(--surface)] w-[150px] h-7 text-xs" />
          </Row>
        </>
      )}
      <p className="text-[10px] text-[color:var(--ink-3)]">Reference lines and trendlines recalculate automatically when filters or drill-downs change the data.</p>
    </div>
  );
}

// ---------- Item 86: Advanced Color Settings (mode switch + rule/category/low-mid-high editors) ----------
export const ADV_COLOR_TYPES = ["bar", "bar_stacked", "line", "area", "combo", "scatter", "bubble", "pie", "donut", "semi_donut", "funnel", "treemap", "tornado", "heatmap", "radar", "point_map"];
const RULE_OPS = [
  { v: "gt", l: ">" }, { v: "gte", l: "≥" }, { v: "lt", l: "<" }, { v: "lte", l: "≤" },
  { v: "eq", l: "=" }, { v: "ne", l: "≠" }, { v: "between", l: "Between" },
];

export function RuleColorsEditor({ opts, patchOpts }) {
  const rules = opts.colorRules || [];
  const set = (arr) => patchOpts({ colorRules: arr });
  const upd = (i, patch) => set(rules.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const move = (i, d) => {
    const j = i + d;
    if (j < 0 || j >= rules.length) return;
    const arr = [...rules];
    [arr[i], arr[j]] = [arr[j], arr[i]];
    set(arr);
  };
  return (
    <div className="space-y-1.5">
      <p className="text-[10px] text-[color:var(--ink-3)]">Rules are evaluated top-down — the first match colors the point.</p>
      {rules.map((r, i) => (
        <div key={i} data-testid="color-rule-row" className="flex items-center gap-1 flex-wrap">
          <Switch checked={r.enabled !== false} onCheckedChange={(v) => upd(i, { enabled: v })} className="scale-75 shrink-0" />
          <Select value={r.op || "gt"} onValueChange={(v) => upd(i, { op: v })}>
            <SelectTrigger className="bg-[color:var(--surface)] w-[58px] h-7 text-xs shrink-0 px-1.5"><SelectValue /></SelectTrigger>
            <SelectContent>{RULE_OPS.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}</SelectContent>
          </Select>
          <Input data-testid="color-rule-value" type="number" value={r.value ?? ""} onChange={(e) => upd(i, { value: e.target.value })} placeholder="value" className="bg-[color:var(--surface)] h-7 text-xs w-[54px] px-1.5" />
          {r.op === "between" && <Input type="number" value={r.value2 ?? ""} onChange={(e) => upd(i, { value2: e.target.value })} placeholder="and" className="bg-[color:var(--surface)] h-7 text-xs w-[54px] px-1.5" />}
          <ColorField testid="color-rule-color" value={r.color} fallback="#22C55E" onChange={(v) => upd(i, { color: v })} />
          <button onClick={() => move(i, -1)} className="text-[color:var(--ink-3)] hover:text-[color:var(--ink)]"><ChevronUp className="w-3 h-3" /></button>
          <button onClick={() => move(i, 1)} className="text-[color:var(--ink-3)] hover:text-[color:var(--ink)]"><ChevronDown className="w-3 h-3" /></button>
          <button data-testid="color-rule-delete" onClick={() => set(rules.filter((_, j) => j !== i))} className="text-[color:var(--danger)]"><X className="w-3 h-3" /></button>
        </div>
      ))}
      <Button data-testid="color-rule-add" size="sm" variant="outline" className="h-7 text-xs bg-[color:var(--surface)]"
        onClick={() => set([...rules, { op: "gt", value: "", color: "#22C55E", enabled: true }])}><Plus className="w-3.5 h-3.5 mr-1" />Rule</Button>
    </div>
  );
}

export function CategoryColorsEditor({ opts, patchOpts }) {
  const map = opts.categoryColors || {};
  const [rows, setRows] = React.useState(() => Object.entries(map));
  React.useEffect(() => {
    // sync in external changes only when the persisted map diverges from local non-empty rows
    const persisted = JSON.stringify(map);
    const local = JSON.stringify(Object.fromEntries(rows.filter(([k]) => k !== "")));
    if (persisted !== local) setRows(Object.entries(map));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(map)]);
  const commit = (list) => {
    setRows(list);
    patchOpts({ categoryColors: Object.fromEntries(list.filter(([k]) => k !== "")) });
  };
  return (
    <div className="space-y-1.5">
      <p className="text-[10px] text-[color:var(--ink-3)]">Assign fixed colors to specific category values (e.g. North → blue). Values keep their colors across data refreshes.</p>
      {rows.map(([k, c], i) => (
        <div key={i} data-testid="cat-color-row" className="flex items-center gap-1">
          <Input data-testid="cat-color-value" value={k} onChange={(e) => { const l = [...rows]; l[i] = [e.target.value, c]; commit(l); }}
            placeholder="Category value" className="bg-[color:var(--surface)] h-7 text-xs flex-1 min-w-0" />
          <ColorField testid="cat-color-color" value={c} fallback="#2B4EFF" onChange={(v) => { const l = [...rows]; l[i] = [k, v]; commit(l); }} />
          <button data-testid="cat-color-delete" onClick={() => commit(rows.filter((_, j) => j !== i))} className="text-[color:var(--danger)]"><X className="w-3 h-3" /></button>
        </div>
      ))}
      <Button data-testid="cat-color-add" size="sm" variant="outline" className="h-7 text-xs bg-[color:var(--surface)]"
        onClick={() => setRows([...rows, ["", "#2B4EFF"]])}><Plus className="w-3.5 h-3.5 mr-1" />Category</Button>
    </div>
  );
}

export function LmhEditor({ opts, patchOpts }) {
  const l = opts.lmh || {};
  const set = (p) => patchOpts({ lmh: { ...l, ...p } });
  return (
    <div className="space-y-1.5">
      <p className="text-[10px] text-[color:var(--ink-3)]">Colors are distributed automatically across the measure's value range (gradient low → medium → high).</p>
      <Row label="Low"><ColorField testid="lmh-low" value={l.low} fallback="#EF4444" onChange={(v) => set({ low: v })} /></Row>
      <Row label="Medium"><ColorField testid="lmh-mid" value={l.mid} fallback="#FFB020" onChange={(v) => set({ mid: v })} /></Row>
      <Row label="High"><ColorField testid="lmh-high" value={l.high} fallback="#22C55E" onChange={(v) => set({ high: v })} /></Row>
    </div>
  );
}

export function AdvancedColorEditor({ opts, patchOpts }) {
  const legacyRange = (opts.rangeColors || []).some((r) => r && r.start !== "" && r.start != null);
  const mode = opts.advColorMode || (legacyRange ? "range" : "default");
  return (
    <div className="space-y-2 pt-1">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs text-[color:var(--ink-3)] flex items-center gap-1"><Paintbrush className="w-3.5 h-3.5" />Advanced color mode</Label>
        <Select value={mode} onValueChange={(v) => patchOpts({ advColorMode: v })}>
          <SelectTrigger data-testid="adv-color-mode" className="bg-[color:var(--surface)] w-[150px] h-7 text-xs shrink-0"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="default">Default palette</SelectItem>
            <SelectItem value="range">Range based</SelectItem>
            <SelectItem value="rule">Rule based</SelectItem>
            <SelectItem value="category">Category mapping</SelectItem>
            <SelectItem value="lmh">Low / Medium / High</SelectItem>
          </SelectContent>
        </Select>
      </div>
      {mode === "range" && <RangeColorsEditor opts={opts} patchOpts={patchOpts} />}
      {mode === "rule" && <RuleColorsEditor opts={opts} patchOpts={patchOpts} />}
      {mode === "category" && <CategoryColorsEditor opts={opts} patchOpts={patchOpts} />}
      {mode === "lmh" && <LmhEditor opts={opts} patchOpts={patchOpts} />}
    </div>
  );
}
